import { useEffect, useRef, useState } from 'react'
import { Download, Loader2, Play, CreditCard, AlertTriangle, Clock, CalendarRange } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { toast } from '@/components/ui/Toast'
import { dateTimeBR } from '@/lib/format'
import {
  AutomationReportsService,
  type AutomationReport,
  type AutomationReportStatus,
} from '@/services/modules/AutomationReportsService'

// Relatórios já construídos (re-exportados aqui para manter o import único em App.tsx).
export { ReportsAgePage } from './AgeReport'
export { ReportsLocationPage } from './LocationReport'
export { ProvidersReportPage } from './ProvidersReport'

const CARD_KEYS = {
  all: 'membership-cards/all-actives',
  new: 'membership-cards/new',
  dateRange: 'membership-cards/date-range',
} as const

function statusVariant(s: AutomationReportStatus): 'gray' | 'green' | 'amber' | 'red' | 'blue' {
  switch (s) {
    case 'CONCLUIDO':   return 'green'
    case 'FALHOU':      return 'red'
    case 'PROCESSANDO': return 'blue'
    default:            return 'amber'
  }
}

/**
 * Relatórios Gerais — por enquanto, relatório de carteirinhas.
 * Duas opções: "Todos os ativos" e "Somente novos" (diferença desde a última
 * geração). Geração assíncrona (fila); guarda o último gerado com data/hora +
 * ícone de download.
 */
export function ReportsGeneralPage() {
  return (
    <>
      <PageHeader
        title="Relatórios Gerais"
        subtitle="Relatório de carteirinhas dos assinantes ativos"
        crumbs={[{ label: 'Comercial' }, { label: 'Relatórios' }, { label: 'Gerais' }]}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CardReportTile
          reportKey={CARD_KEYS.all}
          title="Carteirinhas — Todos os ativos"
          description="Todos os assinantes ativos (titulares e dependentes) com carteirinha gerada."
        />
        <CardReportTile
          reportKey={CARD_KEYS.new}
          title="Carteirinhas — Somente novos"
          description="Apenas os criados desde a última geração (todos ou novos)."
        />
        <DateRangeReportTile
          reportKey={CARD_KEYS.dateRange}
          title="Carteirinhas — Por período"
          description="Assinantes cujo cadastro foi criado no intervalo de datas informado."
        />
      </div>
    </>
  )
}

/**
 * Card do relatório por período: o usuário informa data inicial e final e gera
 * as carteirinhas de todos os assinantes cadastrados (created_at) no intervalo.
 */
function DateRangeReportTile({ reportKey, title, description }: { reportKey: string; title: string; description: string }) {
  const [report, setReport] = useState<AutomationReport | null>(null)
  const [generating, setGenerating] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current) }, [])

  function startPolling(id: string) {
    if (timer.current) window.clearInterval(timer.current)
    timer.current = window.setInterval(async () => {
      try {
        const fresh = await AutomationReportsService.show(id)
        if (fresh.status === 'CONCLUIDO' || fresh.status === 'FALHOU') {
          window.clearInterval(timer.current)
          timer.current = undefined
          setGenerating(false)
          setReport(fresh)
          if (fresh.status === 'CONCLUIDO') toast.success('Relatório pronto.')
          else toast.error(fresh.errorMessage ?? 'Falha ao gerar relatório.')
        }
      } catch { /* tenta de novo na próxima iteração */ }
    }, 4000) as unknown as number
  }

  async function generate() {
    if (!from || !to) { toast.error({ title: 'Informe o período', description: 'Selecione a data inicial e a final.' }); return }
    if (to < from) { toast.error({ title: 'Período inválido', description: 'A data final deve ser igual ou posterior à inicial.' }); return }
    setGenerating(true)
    try {
      const r = await AutomationReportsService.create(reportKey, { from, to })
      startPolling(r.id)
      toast.success('Relatório enfileirado.')
    } catch (e) {
      setGenerating(false)
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao iniciar.')
    }
  }

  const done = report?.status === 'CONCLUIDO' && report.filePath

  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-9 h-9 rounded-md bg-brand-50 text-brand-600 ring-1 ring-brand-100 flex items-center justify-center shrink-0">
            <CalendarRange size={18} />
          </span>
          <h3 className="font-semibold text-gray-800 break-words">{title}</h3>
        </div>
        {report && <Badge variant={statusVariant(report.status)}>{report.status}</Badge>}
      </div>
      <p className="text-xs text-gray-500 mt-2 flex-1">{description}</p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Input label="Data inicial" type="date" value={from} onChange={(e) => setFrom(e.target.value)} clearable={false} />
        <Input label="Data final"   type="date" value={to}   onChange={(e) => setTo(e.target.value)} clearable={false} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button
          size="sm"
          onClick={generate}
          disabled={generating}
          iconLeft={generating ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        >
          {generating ? 'Gerando' : 'Gerar'}
        </Button>
        {done && (
          <a
            href={report!.filePath!}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
          >
            <Download size={14} /> Baixar
          </a>
        )}
        {report?.status === 'FALHOU' && (
          <span className="inline-flex items-center gap-1 text-xs text-rose-600">
            <AlertTriangle size={14} /> Falha
          </span>
        )}
      </div>
    </div>
  )
}

function CardReportTile({ reportKey, title, description }: { reportKey: string; title: string; description: string }) {
  const [report, setReport] = useState<AutomationReport | null>(null)
  const [generating, setGenerating] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    AutomationReportsService.latest(reportKey).then(setReport).catch(() => undefined)
    return () => { if (timer.current) window.clearInterval(timer.current) }
  }, [reportKey])

  function startPolling(id: string) {
    if (timer.current) window.clearInterval(timer.current)
    timer.current = window.setInterval(async () => {
      try {
        const fresh = await AutomationReportsService.show(id)
        if (fresh.status === 'CONCLUIDO' || fresh.status === 'FALHOU') {
          window.clearInterval(timer.current)
          timer.current = undefined
          setGenerating(false)
          setReport(fresh)
          if (fresh.status === 'CONCLUIDO') toast.success('Relatório pronto.')
          else toast.error(fresh.errorMessage ?? 'Falha ao gerar relatório.')
        }
      } catch { /* tenta de novo na próxima iteração */ }
    }, 4000) as unknown as number
  }

  async function generate() {
    setGenerating(true)
    try {
      const r = await AutomationReportsService.create(reportKey)
      startPolling(r.id)
      toast.success('Relatório enfileirado.')
    } catch (e) {
      setGenerating(false)
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao iniciar.')
    }
  }

  const done = report?.status === 'CONCLUIDO' && report.filePath

  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-9 h-9 rounded-md bg-brand-50 text-brand-600 ring-1 ring-brand-100 flex items-center justify-center shrink-0">
            <CreditCard size={18} />
          </span>
          <h3 className="font-semibold text-gray-800 break-words">{title}</h3>
        </div>
        {report && <Badge variant={statusVariant(report.status)}>{report.status}</Badge>}
      </div>
      <p className="text-xs text-gray-500 mt-2 flex-1">{description}</p>

      {/* Último gerado */}
      <div className="mt-3 text-[11px] text-gray-500 flex items-center gap-1.5">
        <Clock size={12} />
        {report?.generatedAt
          ? <>Último gerado em <strong className="text-gray-700">{dateTimeBR(report.generatedAt)}</strong></>
          : 'Nenhum relatório gerado ainda.'}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button
          size="sm"
          onClick={generate}
          disabled={generating}
          iconLeft={generating ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        >
          {generating ? 'Gerando' : 'Gerar'}
        </Button>
        {done && (
          <a
            href={report!.filePath!}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
          >
            <Download size={14} /> Baixar
          </a>
        )}
        {report?.status === 'FALHOU' && (
          <span className="inline-flex items-center gap-1 text-xs text-rose-600">
            <AlertTriangle size={14} /> Falha
          </span>
        )}
      </div>
    </div>
  )
}
